import React, { useState, useEffect } from 'react';
import { X, Send, Check, Phone, User, DollarSign, Shield, Zap, Star, Heart } from 'lucide-react';
import { createPortal } from 'react-dom';
import { saveContact } from '../services/firebase/contactService';
import LegalConsentCheckboxes, { validateConsents, formatConsentsForSubmission } from '../components/legal/LegalConsentCheckboxes';

const FloatingElements = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* נקודות מרקדות */}
      {[...Array(8)].map((_, i) => (
        <div
          key={`dot-${i}`}
          className="absolute w-1 h-1 bg-white/40 rounded-full animate-ping"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 3}s`,
            animationDuration: `${1 + Math.random() * 2}s`
          }}
        />
      ))}
      
      {/* עיגולים זוהרים */}
      {[...Array(4)].map((_, i) => (
        <div
          key={`glow-${i}`}
          className="absolute rounded-full animate-pulse opacity-20"
          style={{
            width: `${15 + Math.random() * 25}px`,
            height: `${15 + Math.random() * 25}px`,
            background: `conic-gradient(from ${Math.random() * 360}deg, #ff006e, #8338ec, #3a86ff, #06ffa5)`,
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 4}s`,
            animationDuration: `${2 + Math.random() * 2}s`,
            filter: 'blur(1px)'
          }}
        />
      ))}
    </div>
  );
};

const LeadModal = ({ isOpen, onClose, onSubmit, sourceContext = null }) => {
 
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    savings: ''
  });
  
  // 🆕 מצב ההסכמות המשפטיות
  const [consents, setConsents] = useState({
    terms: false,
    dataTransfer: false,
    marketing: false
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const savingsOptions = [
    { value: 'up-to-200k', label: 'עד 200 אלף ש"ח' },
    { value: '200k-500k', label: '200-500 אלף ש"ח' },
    { value: 'above-500k', label: '500 אלף ש"ח ומעלה' }
  ];

  useEffect(() => {
    if (isOpen) {
      // איפוס הטופס כשהמודל נפתח
      setFormData({
        name: '',
        phone: '',
        savings: ''
      });
      setConsents({
        terms: false,
        dataTransfer: false,
        marketing: false
      });
      setShowSuccess(false);
    }
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 🆕 טיפול בהסכמות משפטיות
  const handleConsentChange = (newConsents) => {
    setConsents(newConsents);
  };

  const handleSubmit = async () => {
    // בדיקות תקינות בסיסיות
    if (!formData.name.trim()) {
      alert('אנא הכניסו שם מלא');
      return;
    }
    
    if (!formData.phone.trim()) {
      alert('אנא הכניסו מספר טלפון');
      return;
    }
    
    if (!formData.savings) {
      alert('אנא בחרו כמה חסכתם');
      return;
    }
    
    // 🆕 בדיקת הסכמות משפטיות חובה
    const requiredConsents = ['terms', 'dataTransfer'];
    if (!validateConsents(consents, requiredConsents)) {
      alert('יש לאשר את תנאי השימוש והעברת המידע כדי להמשיך');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // הכנת הנתונים לשליחה
      const contactData = {
        name: formData.name.trim(),
        email: '', // אין אימייל במודל
        phone: formData.phone.trim(),
        subject: `בקשה לייעוץ - ${savingsOptions.find(opt => opt.value === formData.savings)?.label}`,
        message: `לקוח מעוניין בייעוץ פיננסי. סכום חיסכון: ${savingsOptions.find(opt => opt.value === formData.savings)?.label}`,
        category: 'פנייה מהירה מטבלת השוואה',
        source: sourceContext ? `${sourceContext.source} - ${sourceContext.category}` : 'lead-modal',
        sourceContext: sourceContext,
        // 🆕 הוספת ההסכמות המשפטיות
        ...formatConsentsForSubmission(consents)
      };

      // שליחה אמיתית ל-Firebase
      const contactId = await saveContact(contactData);
      
      console.log('✅ Lead saved successfully with ID:', contactId);
      
      // הצגת הודעת הצלחה
      setShowSuccess(true);
      
      // סגירה אוטומטית אחרי 2 שניות
      setTimeout(() => {
        setShowSuccess(false);
        onClose();
      }, 2000);
      
    } catch (error) {
      console.error('שגיאה בשליחת הליד:', error);
      alert(`אירעה שגיאה: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* רקע מטושטש */}
      <div 
        className="fixed inset-0 bg-black/70 backdrop-blur-sm" 
        onClick={onClose}
      />
      
      {/* המודל */}
      <div className="relative z-50 w-full max-w-md mx-auto max-h-[90vh] overflow-y-auto">
        <div className="relative bg-gradient-to-br from-indigo-900 via-purple-800 to-pink-700 rounded-2xl shadow-2xl border border-white/20 overflow-hidden animate-scale-up">
          <FloatingElements />
          
          {/* כפתור סגירה */}
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('🔍 Close button clicked');
              if (onClose) {
                console.log('🔍 Calling onClose');
                onClose();
              }
            }}
            className="absolute top-4 left-4 z-20 w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-200"
          >
            <X className="w-4 h-4 text-white" />
          </button>

          {showSuccess ? (
            // מסך הצלחה
            <div className="p-8 text-center text-white relative z-10">
              <div className="w-16 h-16 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full mx-auto flex items-center justify-center animate-bounce mb-4">
                <Check className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2">הפנייה נשלחה! 🎉</h3>
              <p className="text-white/80 text-sm">נחזור אליכם בהקדם האפשרי</p>
              <div className="flex justify-center items-center gap-2 mt-4 text-white/60 text-xs">
                <Heart className="w-4 h-4 text-red-400 animate-pulse" />
                <span>תודה על אמונכם</span>
              </div>
            </div>
          ) : (
            // טופס הליד
            <div className="p-6 relative z-10">
              {/* כותרת דינמית */}
              <div className="text-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 rounded-xl flex items-center justify-center mx-auto mb-3 animate-pulse">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-xl font-bold text-white mb-3">
                  {sourceContext?.source === 'product-page' ? (
                    <>רוצים לעבור במהירות ובקלות ל<span className="text-white">קרן המובילה בקטגוריה</span>?</>
                  ) : sourceContext?.source === 'comparison-table' && sourceContext.firstPlace ? (
                    <>רוצים לעבור במהירות ובקלות ל<span className="text-green-300">"{sourceContext.firstPlace.name}"</span>?</>
                  ) : (
                    <>רוצים לעבור לקרן המתאימה ביותר עבורכם?</>
                  )}
                </h2>
                
                <div className="text-white/90 text-sm leading-relaxed space-y-2 max-w-md mx-auto">
                  <p>
                    השאירו פרטים, ומשווק פנסיוני אובייקטיבי יחזור אליכם עם הצעה מקצועית, 
                    שקופה ומדויקת – המותאמת בדיוק לצרכים שלכם.
                  </p>
                </div>
                
                {/* אייקונים של אמינות */}
                <div className="flex justify-center items-center gap-3 mt-3 text-white/60 text-xs">
                  <div className="flex items-center gap-1">
                    <Shield className="w-3 h-3 text-green-400" />
                    <span>מאובטח</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Star className="w-3 h-3 text-yellow-400" />
                    <span>חינם</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Heart className="w-3 h-3 text-red-400" />
                    <span>מהיר</span>
                  </div>
                </div>
              </div>

              {/* שדות הטופס */}
              <div className="space-y-4">
                {/* שם מלא */}
                <div className="relative">
                  <User className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/60" />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full pr-10 pl-4 py-3 bg-white/10 backdrop-blur-sm border border-white/30 rounded-lg outline-none transition-all duration-300 text-white placeholder-white/60 text-sm focus:border-white/60 focus:bg-white/20"
                    placeholder="שם מלא *"
                    dir="rtl"
                  />
                </div>

                {/* טלפון */}
                <div className="relative">
                  <Phone className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/60" />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full pr-10 pl-4 py-3 bg-white/10 backdrop-blur-sm border border-white/30 rounded-lg outline-none transition-all duration-300 text-white placeholder-white/60 text-sm focus:border-white/60 focus:bg-white/20"
                    placeholder="טלפון נייד *"
                    dir="rtl"
                  />
                </div>

                {/* כמה חסכתי */}
                <div className="relative">
                  <DollarSign className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/60" />
                  <select
                    name="savings"
                    value={formData.savings}
                    onChange={handleInputChange}
                    className="w-full pr-10 pl-4 py-3 bg-white/10 backdrop-blur-sm border border-white/30 rounded-lg outline-none transition-all duration-300 text-white text-sm appearance-none focus:border-white/60 focus:bg-white/20"
                    dir="rtl"
                  >
                    <option value="" className="bg-gray-800 text-white">כמה חסכתם? *</option>
                    {savingsOptions.map(option => (
                      <option key={option.value} value={option.value} className="bg-gray-800 text-white">
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 🆕 רכיב ההסכמות המשפטיות */}
                <div className="bg-white/5 rounded-lg p-3 border border-white/20">
                  <LegalConsentCheckboxes
                    variant="compact"
                    onConsentChange={handleConsentChange}
                    required={['terms', 'dataTransfer']}
                    showDataTransfer={true}
                    showMarketing={true}
                  />
                </div>

                {/* כפתור שליחה */}
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting || !validateConsents(consents, ['terms', 'dataTransfer'])}
                  className={`
                    w-full py-3 rounded-lg text-sm font-bold
                    flex items-center justify-center gap-2
                    transition-all duration-300 transform
                    ${isSubmitting || !validateConsents(consents, ['terms', 'dataTransfer'])
                      ? 'bg-gray-500/50 cursor-not-allowed scale-95' 
                      : 'bg-gradient-to-r from-green-500 via-emerald-600 to-teal-600 hover:from-green-600 hover:via-emerald-700 hover:to-teal-700 hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl'
                    }
                    text-white border border-white/30
                  `}
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>שולח...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span>שלח בקשה ליצירת קשר</span>
                      <Zap className="w-4 h-4 animate-pulse" />
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

     <style>{`
        @keyframes scale-up {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        
        .animate-scale-up {
          animation: scale-up 0.2s ease-out;
        }
      `}</style>
    </div>,
    document.body
  );
};

export default LeadModal;
export { FloatingElements };